import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppSelector } from 'hooks/reduxHooks';
import FileAPI from 'services/api/file-updated.api';
import { CtxDataFile } from 'interface/file/file.interface';
import { PaginationResponse } from 'interface/shared/api.interface';
import { FILE_STATUS } from 'pages/ExperienceCanvas/types';
import { REFETCH_INTERVALS } from 'constants/shared';

const PROCESSING_QUEUED = 'PROCESSING_QUEUED' as const;
type InProgressFileStatus = typeof FILE_STATUS.PROCESSING | typeof PROCESSING_QUEUED;
const IN_PROGRESS_STATUSES: readonly InProgressFileStatus[] = [FILE_STATUS.PROCESSING, PROCESSING_QUEUED];

export function isInProgressStatus(status: string): status is InProgressFileStatus {
  return (IN_PROGRESS_STATUSES as readonly string[]).includes(status);
}

export type FileCounts = {
  total: number;
  processed: number;
  queued: number;
  allCompleted: boolean;
  hasProcessing: boolean;
  hasProcessed: boolean;
};

export function getFileCounts(files: CtxDataFile[]): FileCounts {
  const total = files.length;
  const processed = files.filter(
    (f) => f.status === FILE_STATUS.PROCESSING_SUCCESS || f.status === FILE_STATUS.PROCESSING_FAILED,
  ).length;
  const queued = files.filter((f) => f.status === PROCESSING_QUEUED).length;
  const allCompleted = total > 0 && files.every((f) => f.status === FILE_STATUS.PROCESSING_SUCCESS);
  const hasProcessing = files.some((f) => isInProgressStatus(f.status));
  const hasProcessed = files.some((f) => f.status === FILE_STATUS.PROCESSING_SUCCESS);
  return { total, processed, queued, allCompleted, hasProcessing, hasProcessed };
}

export function formatCompletedTimestamp(epochSeconds: number): string {
  const d = new Date(epochSeconds * 1000);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  const hours = d.getHours() % 12 || 12;
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = d.getHours() >= 12 ? 'pm' : 'am';
  return `${month}/${day}/${year} ${hours}:${minutes}${ampm}`;
}

/**
 * `journey` tags the request with the current journey's `filesUUID`, so only the
 * files belonging to the open experience come back. 
 * `project` drops the tag to list every context-data file in the project
 */
export type CtxDataFilesScope = 'journey' | 'project';

type UseCtxDataFilesOptions = {
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
  sortby?: string;
  sortorder?: 'asc' | 'desc';
  enabled?: boolean;
  scope?: CtxDataFilesScope;
};

export default function useCtxDataFiles(opts: UseCtxDataFilesOptions = {}) {
  const {
    limit = 10,
    offset = 0,
    search,
    status,
    sortby = 'createdAt',
    sortorder = 'desc',
    enabled = true,
    scope = 'journey',
  } = opts;
  const fileAPI = useMemo(() => new FileAPI(), []);
  const filesUUID = useAppSelector((state) => state.te.journey.metadata?.filesUUID || '');
  const isJourneyScoped = scope === 'journey';

  const queryKey = [
    'ctx-data-file',
    scope,
    isJourneyScoped ? filesUUID : '',
    limit,
    offset,
    search ?? '',
    status ?? '',
    sortby,
    sortorder,
  ];

  const query = useQuery<PaginationResponse<CtxDataFile>>({
    queryKey,
    queryFn: () =>
      fileAPI.getAllCtxDataFiles({
        offset,
        limit,
        ...(isJourneyScoped ? { tag: filesUUID } : {}),
        sortby,
        sortorder,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      }),
    keepPreviousData: true,
    refetchInterval: (data) => {
      const currentFiles = data?.data ?? [];
      return currentFiles.some((f) => isInProgressStatus(f.status)) ? REFETCH_INTERVALS.FILE_PROCESSING : false;
    },
    enabled: enabled && (!isJourneyScoped || !!filesUUID),
  });

  const files = useMemo(() => query.data?.data ?? [], [query.data?.data]);
  const counts = useMemo(() => getFileCounts(files), [files]);
  const unprocessed = useMemo(() => files.filter((f) => f.status === FILE_STATUS.UPLOADED), [files]);

  return {
    files,
    counts,
    unprocessed,
    pagination: query.data?.pagination,
    dataUpdatedAt: query.dataUpdatedAt,
    refetch: query.refetch,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
  };
}
